import type { InsuranceVerdict } from '@/hooks/useInsuranceVerdicts'
import { useEffect, useMemo } from 'react'
import { usePreviewPopover } from '../hooks/usePreviewPopover'
import { HoverCard } from '@mantine/core'
import {
  AVAILABILITY_BG,
  AVAILABILITY_PRIORITY,
  DAY_START,
  DAY_END,
  STRIP_HEIGHT,
  STRIP_HEIGHT_BIG,
  fmtTime,
  getVisibleDays,
  sameLocalDay,
  type CalendarEvent,
  type AppointmentStatusValue,
} from './scheduleCalendar.tokens'
import { useBigScreen } from '../hooks/useBigScreen'
import { EventStrip, type AppointmentContext } from './AppointmentBlocks'
import type { AppointmentSearchResult } from './appointment-search'

// Design constants (SCHEDULE_V2_DESIGN.md). Slots are 30 min; a slot row grows
// to fit the busiest day's card stack (variable height), empty rows stay short.
const SLOT = 0.5
const GAPV = 0
// Horizontal gap between cards sharing a time slot, so adjacent cards read as
// separate targets instead of one continuous band.
const GAPH = 6
const PADV = 0
// Fixed width of the "+N" overflow tile in Week view, so the event strips beside
// it get the rest of the column width instead of an equal share.
const NPLUS_W = 34

export interface ScheduleWeekViewProps {
  /** Visible week range from FullCalendar (start = Monday). */
  range: { start: Date; end: Date }
  events: CalendarEvent[]
  isStuffMember: boolean
  isCareCoordinator: boolean
  selectedPractitionerCount: number
  isSendingMagicLink: boolean
  insuranceVerdictMap?: Record<string, InsuranceVerdict>
  returningPatientMap?: Record<string, boolean>
  /** practitionerId → display name, for the preview's doctor row. */
  doctorNameById?: Record<string, string>
  onOpenCall: (encounterId: string) => void
  onReschedule: (appt: CalendarEvent) => void
  onReassign: (appt: CalendarEvent) => void
  onCancel: (encounterId: string) => void
  /** Passed to the preview so the confirmation dialog cannot dismiss it. */
  isCancelConfirmOpen?: boolean
  onStatusChange: (appointmentId: string, status: AppointmentStatusValue) => void
  onSendMagicLink: (encounterId: string) => void
  onCopyMagicLink: (encounterId: string) => void
  /** Refresh the clinic-decision map after a decision is saved in the preview. */
  onClinicDecisionChange?: () => void
  onAppointmentTransferred?: () => void
  onJumpToAppointment?: (result: AppointmentSearchResult) => void
  highlightedEventId?: string | null
  /** An appointment whose preview opens as soon as it is on the calendar (a deep link). */
  autoOpenPreviewId?: string | null
  onAutoOpenPreviewHandled?: () => void
  /** Clicking an empty slot area — opens the create-appointment flow. */
  onSlotSelect: (date: Date) => void
  /** Whether slot creation is currently allowed (single practitioner); controls the affordance. */
  canCreateSlot: boolean
}

/**
 * Custom v2 week grid (Mon–Fri). Replaces FullCalendar's time grid: a slot lays
 * its appointments out in a single row of equal-width tiles — up to 3 in Week
 * (Day allows 5). Beyond that, the last tile becomes a "+N" that collapses the
 * rest. Slot-row heights are computed from the busiest day so rows grow only as
 * needed.
 */
export const ScheduleWeekView = ({
  range,
  events,
  isStuffMember,
  isCareCoordinator,
  selectedPractitionerCount,
  isSendingMagicLink,
  insuranceVerdictMap,
  returningPatientMap,
  doctorNameById,
  onOpenCall,
  onReschedule,
  onReassign,
  onCancel,
  isCancelConfirmOpen,
  onStatusChange,
  onSendMagicLink,
  onCopyMagicLink,
  onClinicDecisionChange,
  onAppointmentTransferred,
  onJumpToAppointment,
  highlightedEventId,
  autoOpenPreviewId,
  onAutoOpenPreviewHandled,
  onSlotSelect,
  canCreateSlot,
}: ScheduleWeekViewProps) => {
  const preview = usePreviewPopover(events)
  const { open: openPreview } = preview
  useEffect(() => {
    if (!autoOpenPreviewId || !events.some((event) => event.id === autoOpenPreviewId)) return
    openPreview(autoOpenPreviewId)
    onAutoOpenPreviewHandled?.()
  }, [autoOpenPreviewId, events, openPreview, onAutoOpenPreviewHandled])
  // Desktop Macs get taller cards (and therefore taller slot rows), so the
  // 13-hour grid fills the vertical space a laptop doesn't have.
  const isBigScreen = useBigScreen()
  const cardH = isBigScreen ? STRIP_HEIGHT_BIG : STRIP_HEIGHT
  // Empty rows are as tall as a single-card row so the grid stays uniform.
  const slotMin = cardH

  // Columns from the visible range: a single day (Day view, shown even on a
  // weekend) or the week's weekdays (Mon–Fri).
  const days = useMemo(() => getVisibleDays(range), [range])

  // Cards lay out in a single row of up to ROW_MAX tiles — 5 in Day, 3 in Week.
  // Beyond that a "+N" tile collapses the rest.
  const isDay = days.length === 1
  const ROW_MAX = isDay ? 5 : 3
  const COLS = ROW_MAX
  // Real cards shown alongside the "+N" tile when overflowing (e.g. Week shows
  // 2 cards + "+N", Day shows 4 cards + "+N").
  const OVERFLOW_VISIBLE = ROW_MAX - 1

  const slotStarts = useMemo(() => {
    const out: number[] = []
    for (let t = DAY_START; t < DAY_END - 0.001; t += SLOT) out.push(Math.round(t * 2) / 2)
    return out
  }, [])

  // appts grouped by `${dayIdx}|${slotStart}`
  const byCell = useMemo(() => {
    const map = new Map<string, CalendarEvent[]>()
    for (const ev of events) {
      if (ev.display === 'background' || !ev.start) continue
      const d = new Date(ev.start)
      const dayIdx = days.findIndex((day) => sameLocalDay(day, d))
      if (dayIdx < 0) continue
      const slot = d.getHours() + (d.getMinutes() >= 30 ? 0.5 : 0)
      if (slot < DAY_START || slot >= DAY_END) continue
      const key = `${dayIdx}|${slot}`
      const list = map.get(key)
      if (list) list.push(ev)
      else map.set(key, [ev])
    }
    for (const list of map.values()) list.sort((a, b) => a.start.localeCompare(b.start))
    return map
  }, [events, days])

  // Height per slot row: driven by the busiest day in that row.
  const slotH = useMemo(() => {
    const h: Record<number, number> = {}
    for (const st of slotStarts) {
      let maxCells = 0
      for (let di = 0; di < days.length; di++) {
        const n = byCell.get(`${di}|${st}`)?.length ?? 0
        // Displayed tiles: all appts up to ROW_MAX, else OVERFLOW_VISIBLE + "+N".
        const cells = n <= ROW_MAX ? n : OVERFLOW_VISIBLE + 1
        if (cells > maxCells) maxCells = cells
      }
      const rows = maxCells === 0 ? 0 : Math.ceil(maxCells / COLS)
      h[st] = rows === 0 ? slotMin : rows * cardH + (rows - 1) * GAPV + PADV
    }
    return h
  }, [byCell, slotStarts, days.length, ROW_MAX, OVERFLOW_VISIBLE, COLS, cardH, slotMin])

  // Availability (green/red) background slots — present only when a single
  // practitioner is selected (multi-practitioner loads no slots).
  const availability = useMemo(
    () => events.filter((e) => e.display === 'background' && e.start && e.end),
    [events],
  )

  // Map an absolute clock hour to a pixel offset within the variable-height grid.
  const yFor = (hour: number) => {
    const h = Math.max(DAY_START, Math.min(DAY_END, hour))
    let cum = 0
    for (const st of slotStarts) {
      if (h <= st) return cum
      if (h < st + SLOT) return cum + ((h - st) / SLOT) * slotH[st]
      cum += slotH[st]
    }
    return cum
  }

  const todayIdx = days.findIndex((d) => sameLocalDay(d, new Date()))
  const nowTop = useMemo(() => {
    if (todayIdx < 0) return null
    const now = new Date()
    const nowH = now.getHours() + now.getMinutes() / 60
    if (nowH < DAY_START || nowH >= DAY_END) return null
    let cum = 0
    for (const st of slotStarts) {
      if (nowH >= st && nowH < st + SLOT) return cum + ((nowH - st) / SLOT) * slotH[st]
      cum += slotH[st]
    }
    return null
  }, [todayIdx, slotStarts, slotH])

  const slotLabel = (st: number) => {
    const h = Math.floor(st)
    const isHour = Math.abs(st - h) < 0.01
    const ap = h >= 12 ? 'PM' : 'AM'
    const hh = h % 12 || 12
    return { text: isHour ? `${hh} ${ap}` : `${hh}:30`, isHour }
  }

  // Shared context handed to every appointment block (card kebab, detail
  // preview, "+N" mini rows) — see AppointmentBlocks.tsx.
  const ctx: AppointmentContext = {
    isStuffMember,
    isCareCoordinator,
    selectedPractitionerCount,
    isSendingMagicLink,
    insuranceVerdictMap,
    returningPatientMap,
    doctorNameById,
    onOpenCall,
    onReschedule,
    onReassign,
    onCancel,
    isCancelConfirmOpen,
    onStatusChange,
    onSendMagicLink,
    onCopyMagicLink,
    onClinicDecisionChange,
    onAppointmentTransferred,
    onJumpToAppointment,
    highlightedEventId,
  }

  // Preview popover side: Day opens below; Week opens toward the grid interior
  // (left for the later columns) so it doesn't clip at the edge.
  const previewPos = (sideLeft: boolean) =>
    isDay ? 'bottom-start' : sideLeft ? 'left-start' : 'right-start'

  const renderCell = (di: number, st: number, sideLeft: boolean) => {
    const cellAppts = byCell.get(`${di}|${st}`) ?? []
    const hasOverflow = cellAppts.length > ROW_MAX
    const ordered =
      hasOverflow && highlightedEventId
        ? [
            ...cellAppts.filter((appt) => appt.id === highlightedEventId),
            ...cellAppts.filter((appt) => appt.id !== highlightedEventId),
          ]
        : cellAppts
    const shown = hasOverflow ? ordered.slice(0, OVERFLOW_VISIBLE) : ordered
    const hidden = hasOverflow ? ordered.slice(OVERFLOW_VISIBLE) : []
    const tileCount = shown.length + (hasOverflow ? 1 : 0)
    const isHour = Math.abs(st - Math.floor(st)) < 0.01
    const stop = (e: React.MouseEvent) => {
      e.preventDefault()
      e.stopPropagation()
    }
    // Clicking empty cell area (card/overflow clicks stop propagation) starts a
    // new appointment at this slot's time. Only future slots are clickable.
    const slotDate = new Date(days[di])
    slotDate.setHours(Math.floor(st), st % 1 ? 30 : 0, 0, 0)
    const clickable = canCreateSlot && slotDate.getTime() > Date.now()
    return (
      <div
        key={st}
        onClick={clickable ? () => onSlotSelect(slotDate) : undefined}
        className={`grid ${clickable ? 'cursor-pointer hover:bg-black/[0.015]' : ''}`}
        style={{
          height: `${slotH[st]}px`,
          boxSizing: 'border-box',
          gridTemplateColumns:
            hasOverflow && !isDay
              ? `repeat(${shown.length}, minmax(0, 1fr)) ${NPLUS_W}px`
              : `repeat(${Math.max(tileCount, 1)}, minmax(0, 1fr))`,
          gridAutoRows: `${cardH}px`,
          columnGap: `${GAPH}px`,
          rowGap: `${GAPV}px`,
          alignContent: 'start',
          padding: 0,
          borderTop: `1px solid ${isHour ? '#D4D9E0' : '#EEF0F3'}`,
        }}
      >
        {shown.map((appt) => (
          <div key={appt.id} className="min-w-0">
            <EventStrip
              appt={appt}
              ctx={ctx}
              preview={preview}
              previewPosition={previewPos(sideLeft)}
              big={isBigScreen}
            />
          </div>
        ))}
        {hasOverflow && (
          <div className="min-w-0">
            <HoverCard
              position={sideLeft ? 'bottom-start' : 'bottom-end'}
              width={240}
              withinPortal
              radius={12}
              openDelay={60}
              closeDelay={180}
              styles={{
                dropdown: {
                  padding: 6,
                  border: '1px solid #E4E7EB',
                  boxShadow: '0 14px 36px rgba(16,24,40,.2)',
                },
              }}
            >
              <HoverCard.Target>
                <div
                  onClick={stop}
                  className="flex h-full cursor-pointer flex-col items-center justify-center rounded-lg border border-dashed border-[#BFC6D0] bg-[#EDEFF3] text-[12px] font-bold text-[#5A616B]"
                >
                  +{hidden.length}
                </div>
              </HoverCard.Target>
              <HoverCard.Dropdown>
                <div className="px-[9px] pt-[7px] pb-2 text-[11px] font-bold tracking-[.4px] text-[#9AA0A8] uppercase">
                  Also at {fmtTime(slotDate.toISOString())}
                </div>
                <div className="flex flex-col gap-[5px]">
                  {hidden.map((m) => (
                    <div key={m.id} style={{ height: cardH }}>
                      <EventStrip
                        appt={m}
                        ctx={ctx}
                        preview={preview}
                        previewPosition={previewPos(sideLeft)}
                        previewWithinPortal={false}
                        big={isBigScreen}
                      />
                    </div>
                  ))}
                </div>
              </HoverCard.Dropdown>
            </HoverCard>
          </div>
        )}
      </div>
    )
  }

  return (
    <div className="mt-3 overflow-hidden rounded-[14px] border border-[#EAECEF] bg-white">
      {/* Day headers */}
      <div className="flex border-b border-[#EAECEF]">
        <div className="w-16 shrink-0" />
        {days.map((day, di) => {
          const today = di === todayIdx
          const weekday = day.toLocaleDateString(undefined, { weekday: 'short' })
          return (
            <div
              key={day.toISOString()}
              className="flex flex-1 justify-center border-l border-[#EAECEF] py-1"
            >
              <div
                className={`inline-flex items-center gap-1.5 ${today ? 'rounded-lg bg-[#EAF1FE] px-2.5 py-1' : ''}`}
              >
                <span
                  className={`text-[13px] font-semibold tracking-[.3px] ${today ? 'text-[#2D74E0]' : 'text-[#8A8F98]'}`}
                >
                  {weekday}
                </span>
                <span
                  className={`text-[13px] font-bold ${today ? 'text-[#2D74E0]' : 'text-[#1F2733]'}`}
                >
                  {day.getDate()}
                </span>
              </div>
            </div>
          )
        })}
      </div>

      {/* Body: time gutter + day columns */}
      <div className="flex">
        <div className="w-16 shrink-0">
          {slotStarts.map((st) => {
            const { text, isHour } = slotLabel(st)
            return (
              <div
                key={st}
                className="pr-2.5 text-right"
                style={{
                  height: `${slotH[st]}px`,
                  boxSizing: 'border-box',
                  paddingTop: '3px',
                  fontSize: isHour ? '11px' : '10px',
                  fontWeight: isHour ? 700 : 600,
                  color: isHour ? '#5A616B' : '#B2B8C0',
                }}
              >
                {text}
              </div>
            )
          })}
        </div>
        {days.map((day, di) => {
          const today = di === todayIdx
          const sideLeft = di >= 2
          const dayBands = availability.filter((a) => sameLocalDay(new Date(a.start), day))
          return (
            <div
              key={day.toISOString()}
              className="relative min-w-0 flex-1 border-l border-[#EAECEF]"
              style={{
                background: today ? 'rgba(45,116,224,.045)' : undefined,
                isolation: 'isolate',
              }}
            >
              {/* Availability fills sit behind the cells (z-0). Resolved per
                  30-min slot so overlapping slots never blend: the highest
                  priority (blocked > busy > free) wins and each slot shows one
                  color — no yellow bleeding into a neighbouring slot. */}
              {slotStarts.map((st) => {
                const mid = st + SLOT / 2
                let best: string | undefined
                let bestPriority = 0
                for (const band of dayBands) {
                  const s = new Date(band.start)
                  const e = new Date(band.end)
                  const sh = s.getHours() + s.getMinutes() / 60
                  const eh = e.getHours() + e.getMinutes() / 60
                  if (mid < sh || mid >= eh) continue
                  const priority = AVAILABILITY_PRIORITY[band.slotType ?? ''] ?? 0
                  if (priority > bestPriority) {
                    bestPriority = priority
                    best = band.slotType
                  }
                }
                const color = best ? AVAILABILITY_BG[best] : undefined
                if (!color) return null
                // Free (green) slots are the "create a new event" targets. Give
                // them their own click handler and sit them above the cells
                // (z-2) so the click is reliably captured — Day and Week behave
                // identically. Blocked/busy fills stay decorative, behind the
                // cells (pointer-events-none).
                const slotDate = new Date(day)
                slotDate.setHours(Math.floor(st), st % 1 ? 30 : 0, 0, 0)
                const freeClickable =
                  best === 'free' && canCreateSlot && slotDate.getTime() > Date.now()
                return (
                  <div
                    key={`avail-${st}`}
                    onClick={freeClickable ? () => onSlotSelect(slotDate) : undefined}
                    className={`absolute right-0 left-0 ${
                      freeClickable
                        ? 'cursor-pointer transition-[filter] hover:brightness-95'
                        : 'pointer-events-none'
                    }`}
                    style={{
                      top: `${yFor(st)}px`,
                      height: `${yFor(st + SLOT) - yFor(st)}px`,
                      background: color,
                      zIndex: freeClickable ? 2 : 0,
                    }}
                  />
                )
              })}
              {today && nowTop != null && (
                <div
                  className="pointer-events-none absolute right-0 left-0 z-20 h-0.5 bg-[#E5484D]"
                  style={{ top: `${nowTop}px` }}
                >
                  <span className="absolute top-[-3px] left-[-4px] h-2 w-2 rounded-full bg-[#E5484D]" />
                </div>
              )}
              <div className="relative" style={{ zIndex: 1 }}>
                {slotStarts.map((st) => renderCell(di, st, sideLeft))}
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}
